// PLAN-judgment-cockpit Phase B (2026-05-15)
// Article への inline feedback action (version scope: like / concern / dislike / comment)
// + lineage scope (reuse_ok / reuse_ng)。書き込み後 onWrite callback で UI 再 fetch。

import { useState } from 'react'
import { createFeedback, type FeedbackType } from '../../api/feedback'
import { useToast } from './common/Toast'

interface Props {
  articleId: string
  /** 現 Article の decision_hash (version scope の payload に必要) */
  decisionHash?: string | null
  /** 書き込み後の通知 callback (UI 側で row を update / refetch) */
  onWrite?: () => void
  /** compact mode (Review Queue の inline 用) */
  compact?: boolean
  /** disabled (decisionHash 不在で version scope 書き込み不能のとき外側から制御) */
  disabledVersion?: boolean
}

const VERSION_BUTTONS: { type: FeedbackType; label: string; tooltip: string }[] = [
  { type: 'like', label: '👍', tooltip: 'like (この版の判断は良い)' },
  { type: 'concern', label: '⚠️', tooltip: 'concern (違和感がある)' },
  { type: 'dislike', label: '👎', tooltip: 'dislike (この版の判断は良くない)' },
]
const LINEAGE_BUTTONS: { type: FeedbackType; label: string; tooltip: string }[] = [
  { type: 'reuse_ok', label: '♻️', tooltip: 'reuse_ok (lineage を再利用してよい)' },
  { type: 'reuse_ng', label: '🚫', tooltip: 'reuse_ng (lineage を再利用すべきでない)' },
]

export function JudgmentFeedbackActions({ articleId, decisionHash, onWrite, compact, disabledVersion }: Props) {
  const { show } = useToast()
  const [busy, setBusy] = useState<FeedbackType | null>(null)

  const doWrite = async (type: FeedbackType) => {
    const isLineage = type === 'reuse_ok' || type === 'reuse_ng'
    if (!isLineage && !decisionHash) {
      show('decision_hash がないため version scope feedback は書き込めません', { kind: 'error' })
      return
    }
    setBusy(type)
    try {
      await createFeedback(articleId, {
        feedback_scope: isLineage ? 'lineage' : 'version',
        feedback_type: type,
        decision_hash: isLineage ? null : decisionHash,
      })
      show(`${type} を記録しました`, { kind: 'success' })
      onWrite?.()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      show(`feedback 書き込み失敗: ${msg}`, { kind: 'error' })
    } finally {
      setBusy(null)
    }
  }

  const sz = compact ? 'h-7 w-7 text-base' : 'h-8 w-8 text-lg'

  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label="Judgment feedback">
      {VERSION_BUTTONS.map((b) => (
        <button
          key={b.type}
          type="button"
          onClick={(e) => { e.stopPropagation(); void doWrite(b.type) }}
          disabled={busy !== null || disabledVersion || !decisionHash}
          title={b.tooltip}
          aria-label={b.tooltip}
          className={`${sz} rounded border border-border-subtle bg-bg-surface hover:bg-bg-overlay disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center`}
        >
          {busy === b.type ? '…' : b.label}
        </button>
      ))}
      <span className="mx-1 text-text-subtle" aria-hidden>·</span>
      {LINEAGE_BUTTONS.map((b) => (
        <button
          key={b.type}
          type="button"
          onClick={(e) => { e.stopPropagation(); void doWrite(b.type) }}
          disabled={busy !== null}
          title={b.tooltip}
          aria-label={b.tooltip}
          className={`${sz} rounded border border-border-subtle bg-bg-surface hover:bg-bg-overlay disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center`}
        >
          {busy === b.type ? '…' : b.label}
        </button>
      ))}
    </div>
  )
}
